import React from 'react';
import { Card, List, Tag, Tooltip, Empty } from 'antd';
import { 
  DashboardOutlined, 
  CheckCircleOutlined, 
  ExclamationCircleOutlined,
  CloseCircleOutlined,
  EnvironmentOutlined
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';

const UltimosSensoresWidget = ({ sensores }) => {
  if (!sensores || sensores.length === 0) {
    return (
      <Card 
        title="Últimos Sensores Consultados" 
        className="dashboard-widget dashboard-widget--sensores"
      >
        <Empty 
          description="No hay sensores consultados recientemente"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      </Card>
    );
  }

  const getPeligrosidadColor = (peligrosidad) => {
    switch (peligrosidad) {
      case 'normal':
        return 'success';
      case 'advertencia':
        return 'warning';
      case 'peligro':
        return 'error';
      default:
        return 'default';
    }
  };

  const getPeligrosidadIcon = (peligrosidad) => {
    switch (peligrosidad) {
      case 'normal':
        return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
      case 'advertencia':
        return <ExclamationCircleOutlined style={{ color: '#faad14' }} />;
      case 'peligro':
        return <CloseCircleOutlined style={{ color: '#ff4d4f' }} />;
      default:
        return <DashboardOutlined />;
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Ahora';
    if (diffMins < 60) return `${diffMins}m`;
    if (diffHours < 24) return `${diffHours}h`;
    if (diffDays < 7) return `${diffDays}d`;
    return date.toLocaleDateString('es-ES');
  };

  const formatValue = (valor, unidades) => {
    if (valor === null || valor === undefined) return 'N/A';
    return `${Number(valor).toLocaleString('es-ES', { 
      maximumFractionDigits: 2 
    })} ${unidades}`;
  };

  return (
    <Card 
      title="Últimos Sensores Consultados" 
      className="dashboard-widget dashboard-widget--sensores"
      extra={<DashboardOutlined />}
    >
      <List
        dataSource={sensores.slice(0, 5)}
        renderItem={(sensor) => (
          <List.Item
            key={sensor._id}
            className="sensor-item"
            actions={[
              <Tooltip key={`tooltip-${sensor._id}`} title={`Dispositivo ${sensor.estatusDispositivo ? 'Activo' : 'Inactivo'}`}>
                <Tag 
                  color={sensor.estatusDispositivo ? 'success' : 'error'}
                  icon={sensor.estatusDispositivo ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
                >
                  {sensor.estatusDispositivo ? 'Activo' : 'Inactivo'}
                </Tag>
              </Tooltip>
            ]}
          >
            <List.Item.Meta
              avatar={
                <div className="sensor-item__avatar">
                  {sensor.ultimoValor?.peligrosidad 
                    ? getPeligrosidadIcon(sensor.ultimoValor.peligrosidad)
                    : <DashboardOutlined />
                  }
                </div>
              }
              title={
                <div className="sensor-item__title">
                  <Link to={`/info-sensor/${sensor._id}`} className="sensor-item__name">
                    {sensor.alias || sensor.nombre}
                  </Link>
                  <Tag size="small" color="blue">
                    {sensor.tipo?.nombre || 'Tipo no disponible'}
                  </Tag>
                </div>
              }
              description={
                <div className="sensor-item__description">
                  <div className="sensor-item__location">
                    <EnvironmentOutlined />
                    <Tooltip title={
                      sensor.tarjeta?.elemento?.proyecto?.nombre 
                        ? `Proyecto: ${sensor.tarjeta.elemento.proyecto.nombre}`
                        : 'Proyecto no disponible'
                    }>
                      <span>
                        {sensor.tarjeta?.elemento?.nombre || 'Elemento no disponible'}
                      </span>
                    </Tooltip>
                  </div>
                  
                  <div className="sensor-item__value">
                    {sensor.ultimoValor ? (
                      <span className="sensor-item__measurement">
                        <strong>{formatValue(sensor.ultimoValor.valor, sensor.unidades)}</strong>
                        <Tag 
                          size="small" 
                          color={getPeligrosidadColor(sensor.ultimoValor.peligrosidad)}
                        >
                          {sensor.ultimoValor.peligrosidad}
                        </Tag>
                      </span>
                    ) : (
                      <span className="sensor-item__no-data">Sin datos</span>
                    )}
                  </div>

                  <div className="sensor-item__timestamp">
                    {sensor.fechaConsulta ? (
                      <Tooltip title={`Consultado: ${new Date(sensor.fechaConsulta).toLocaleString('es-ES')}`}>
                        <span>Hace {formatTimestamp(sensor.fechaConsulta)}</span>
                      </Tooltip>
                    ) : (
                      <Tooltip title="Fecha de consulta no disponible">
                        <span>Sin fecha</span>
                      </Tooltip>
                    )}
                  </div>
                </div>
              }
            />
          </List.Item>
        )}
      />
    </Card>
  );
};

UltimosSensoresWidget.propTypes = {
  sensores: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string.isRequired,
      nombre: PropTypes.string.isRequired,
      alias: PropTypes.string,
      unidades: PropTypes.string.isRequired,
      tipo: PropTypes.shape({
        _id: PropTypes.string.isRequired,
        nombre: PropTypes.string.isRequired
      }).isRequired,
      tarjeta: PropTypes.shape({
        _id: PropTypes.string.isRequired,
        nombre: PropTypes.string.isRequired,
        elemento: PropTypes.shape({
          _id: PropTypes.string.isRequired,
          nombre: PropTypes.string.isRequired,
          proyecto: PropTypes.shape({
            _id: PropTypes.string.isRequired,
            nombre: PropTypes.string.isRequired
          }).isRequired
        }).isRequired
      }), // Removido .isRequired porque puede ser null
      ultimoValor: PropTypes.shape({
        valor: PropTypes.number,
        fecha: PropTypes.string,
        peligrosidad: PropTypes.oneOf(['normal', 'advertencia', 'peligro'])
      }),
      fechaConsulta: PropTypes.string, // Removido .isRequired porque puede ser null
      estatusDispositivo: PropTypes.bool.isRequired
    })
  )
};

UltimosSensoresWidget.defaultProps = {
  sensores: []
};

export default UltimosSensoresWidget;
